import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import AdminTheme from '../AdminTheme';

/**
 * The shop catalog list (#5445).
 *
 * An island rather than ERB, and a deliberate one: the filters apply as you change
 * them, with no reload and no round trip. That is the case CLAUDE.md's HTML-first
 * rule carves out, and it is only affordable because the page already holds every
 * row -- the server computes each product's status to draw its badge, so filtering in
 * the browser hides rows that are already in hand.
 *
 * The other reason is plainer: the ERB filter bar's `.sp-select` fields rendered as
 * bare text with a chevron, so the controls did not read as controls at all. MUI's
 * outlined fields have a visible box, a floating label and a focus ring.
 *
 * This component decides NOTHING about the products. Whether one is live, which kind
 * it is, what it is still missing and where its links go are all computed server-side
 * (Shop::CatalogRows); this filters, sorts and draws.
 */

// Colour per kind. Every type used to be the same grey chip, which is the opposite of
// differentiating them.
const TYPE_COLOR = {
  package: 'primary',
  print: 'info',
  digital: 'success',
  printess: 'warning',
  addon: 'default',
};

const STATUS_COLOR = { live: 'success', ready: 'info', draft: 'warning' };
const STATUS_TEXT = { live: 'Live', ready: 'Ready', draft: 'Draft' };

const COLUMNS = [
  { id: 'name', label: 'Name' },
  { id: 'type_label', label: 'Kind' },
  { id: 'season', label: 'Season' },
  { id: 'variants', label: 'Packages', numeric: true },
  { id: 'styles', label: 'Styles', numeric: true },
  { id: 'schools', label: 'Schools', numeric: true },
  { id: 'status', label: 'Status' },
];

// Draft first when sorting by status: it is the pile that needs work, and on the live
// catalog it is fourteen of nineteen.
const STATUS_RANK = { draft: 0, ready: 1, live: 2 };

// MUI's default button padding is generous for a table of four actions per row --
// enough, with nowrap, to push the table past its container.
const COMPACT = { minWidth: 0, px: 0.75, textTransform: 'none' };

const compare = (a, b, key) => {
  if (key === 'status') return (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
  const left = a[key];
  const right = b[key];
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left ?? '').localeCompare(String(right ?? ''));
};

const ShopCatalogTable = ({
  rows, types, statuses, seasons, newProductPath, csrfToken,
}) => {
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [season, setSeason] = useState('');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = rows.filter((row) => {
      if (type && row.type !== type) return false;
      if (status && row.status !== status) return false;
      // 'none' is a real answer: a product with no season is a draft nobody finished.
      if (season === 'none' && row.season) return false;
      if (season && season !== 'none' && row.season !== season) return false;
      if (needle && !row.name.toLowerCase().includes(needle)) return false;
      return true;
    });
    const sorted = [...list].sort((a, b) => compare(a, b, sortBy));
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [rows, type, status, season, query, sortBy, sortDir]);

  const filtering = !!(type || status || season || query.trim());

  const sortOn = (id) => {
    if (sortBy === id) return setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    setSortBy(id);
    return setSortDir('asc');
  };

  const clear = () => {
    setType('');
    setStatus('');
    setSeason('');
    setQuery('');
  };

  // A plain form post, not fetch: archiving navigates, and rails-ujs is what turns a
  // data-method link into a POST with the CSRF token. Building the form keeps this
  // island out of that business entirely.
  const archive = (row) => {
    if (!window.confirm(`Archive "${row.name}"? It will stop being offered.`)) return;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = row.paths.archive;
    form.innerHTML = '<input type="hidden" name="_method" value="delete">'
      + `<input type="hidden" name="authenticity_token" value="${csrfToken}">`;
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
          <TextField
            select
            label="Kind"
            size="small"
            value={type}
            sx={{ width: 220 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setType(e.target.value)}
          >
            <MenuItem value="">{`All kinds (${rows.length})`}</MenuItem>
            {types.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{`${opt.label} (${opt.count})`}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="How far along"
            size="small"
            value={status}
            sx={{ width: 260 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">Any</MenuItem>
            {statuses.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{`${opt.label} (${opt.count})`}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Season"
            size="small"
            value={season}
            sx={{ width: 180 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setSeason(e.target.value)}
          >
            <MenuItem value="">All seasons</MenuItem>
            {seasons.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Search by name"
            size="small"
            value={query}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
            onChange={(e) => setQuery(e.target.value)}
          />

          {filtering ? <Button sx={{ mt: 0.5 }} onClick={clear}>Clear</Button> : null}

          <Box sx={{ flex: 1 }} />
          <Button variant="contained" color="success" href={newProductPath} sx={{ mt: 0.5 }}>
            + New product
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {filtering
            ? `Showing ${filtered.length} of ${rows.length} products.`
            : `${rows.length} products.`}
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.id} align={col.numeric ? 'right' : 'left'}>
                  <TableSortLabel
                    active={sortBy === col.id}
                    direction={sortBy === col.id ? sortDir : 'asc'}
                    onClick={() => sortOn(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Button
                    href={row.paths.edit}
                    sx={{
                      textTransform: 'none', p: 0, minWidth: 0, textAlign: 'left', lineHeight: 1.3,
                    }}
                  >
                    {row.name}
                  </Button>
                </TableCell>
                <TableCell>
                  <Chip size="small" color={TYPE_COLOR[row.type] || 'default'} label={row.type_label} />
                </TableCell>
                <TableCell>
                  {row.season || <Typography variant="caption" color="text.secondary">none</Typography>}
                </TableCell>
                <TableCell align="right">{row.variants}</TableCell>
                <TableCell align="right">{row.styles}</TableCell>
                <TableCell align="right">{row.schools}</TableCell>
                <TableCell>
                  {/* The badge says the state; the tooltip says what is missing, which
                      is the question a Draft immediately raises. */}
                  <Tooltip
                    title={row.missing.length ? `Still needs: ${row.missing.join(', ')}` : 'Nothing missing'}
                  >
                    <Chip
                      size="small"
                      color={STATUS_COLOR[row.status] || 'warning'}
                      label={STATUS_TEXT[row.status] || 'Draft'}
                    />
                  </Tooltip>
                  {row.missing.length ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {`needs ${row.missing.length} thing${row.missing.length === 1 ? '' : 's'}`}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  {/* Wrapping, NOT nowrap: four actions held on one line pushed the
                      table wider than its container and put a horizontal scrollbar
                      under a page that otherwise fits. They wrap at a narrow width
                      instead, and the buttons are tight enough that they rarely need
                      to. */}
                  <Stack
                    direction="row"
                    spacing={0.25}
                    justifyContent="flex-end"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {/* One link, two names. Since the guided setup became the only
                        editor these go to the same place, so showing both was a
                        redundant button on every row -- and the fourth button is what
                        pushed the table past its container. A draft's next move is to
                        finish it, so that one is prominent; anything else just opens. */}
                    {row.status === 'draft' ? (
                      <Button size="small" variant="contained" href={row.paths.wizard} sx={COMPACT}>
                        Finish setup
                      </Button>
                    ) : (
                      <Button size="small" href={row.paths.edit} sx={COMPACT}>Edit</Button>
                    )}
                    <Button size="small" href={row.paths.assign} sx={COMPACT}>Schools</Button>
                    <Button size="small" color="error" sx={COMPACT} onClick={() => archive(row)}>
                      Archive
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1}>
                  <Typography variant="body2" color="text.secondary">
                    {filtering
                      ? 'No product matches those filters.'
                      : 'No products yet. Create one to get started.'}
                    {filtering ? <Button size="small" onClick={clear}>Clear them</Button> : null}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const rowShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  type: PropTypes.string,
  status: PropTypes.string,
  missing: PropTypes.arrayOf(PropTypes.string),
  paths: PropTypes.shape({
    edit: PropTypes.string,
    wizard: PropTypes.string,
    assign: PropTypes.string,
    archive: PropTypes.string,
  }),
});

const optionShape = PropTypes.shape({
  value: PropTypes.string, label: PropTypes.string, count: PropTypes.number,
});

ShopCatalogTable.propTypes = {
  rows: PropTypes.arrayOf(rowShape),
  types: PropTypes.arrayOf(optionShape),
  statuses: PropTypes.arrayOf(optionShape),
  seasons: PropTypes.arrayOf(optionShape),
  newProductPath: PropTypes.string,
  csrfToken: PropTypes.string,
};

ShopCatalogTable.defaultProps = {
  rows: [], types: [], statuses: [], seasons: [], newProductPath: '', csrfToken: '',
};

export { ShopCatalogTable };

export default (props) => (
  <AdminTheme>
    <ShopCatalogTable {...props} />
  </AdminTheme>
);
